import React, { useEffect, useMemo, useState } from "react";
import { api } from "../Assets/assetsShared";
import "./EmployeeLogSummary.css";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const thisYear = () => String(new Date().getFullYear());
const thisMonth = () => String(new Date().getMonth() + 1).padStart(2, "0");
const fmt = (n) => Number(Number(n || 0).toFixed(2));
const monthOf = (e) => String(e.date || "").slice(5, 7);
const isAdminRole = (role) =>
  String(role || "")
    .trim()
    .toLowerCase() === "admin";

// "13:05" -> "01:05 PM"
const showTime = (value) => {
  if (!value) return "";
  const [h, m] = String(value).split(":").map(Number);
  const hour = h % 12 || 12;
  return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")} ${
    h >= 12 ? "PM" : "AM"
  }`;
};

export default function EmployeeLogSummary() {
  const [year, setYear] = useState(thisYear());
  const [month, setMonth] = useState(thisMonth()); // "01".."12" or "all"
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // { id, name } or null

  const [employees, setEmployees] = useState([]);
  const [years, setYears] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  // null = not checked yet, true = may see all employees, false = may not
  const [canSeeAll, setCanSeeAll] = useState(null);
  const [myRole, setMyRole] = useState("");

  /* ---------- all employees (admins are not included) ---------- */
  useEffect(() => {
    api
      .get("/timesheet/log/employees")
      .then((res) => setEmployees(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMessage("Could not load the employee list."));
  }, []);

  /* ---------- access + years that have entries ---------- */
  useEffect(() => {
    api
      .get("/timesheet/log/summary")
      .then((res) => {
        const list = (res.data?.years || []).map((y) => y.year);
        setYears(list);
        setCanSeeAll(Boolean(res.data?.isAdmin));
        setMyRole(res.data?.role || "");

        // no entries in the current year: jump to the latest year that has data
        setYear((prev) =>
          !list.length || list.includes(prev) || prev === thisYear()
            ? prev
            : list[0],
        );
      })
      .catch(() => setYears([]));
  }, []);

  /* ---------- entries of everyone for the selected year (admins left out) ---------- */
  useEffect(() => {
    setLoading(true);

    api
      .get("/timesheet/log", { params: { year } })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setEntries(list.filter((e) => !isAdminRole(e.role)));
      })
      .catch(() => {
        setEntries([]);
        setMessage("Could not load the log.");
      })
      .finally(() => setLoading(false));
  }, [year]);

  const yearOptions = useMemo(
    () =>
      Array.from(new Set([...years, thisYear(), year])).sort((a, b) =>
        b.localeCompare(a),
      ),
    [years, year],
  );

  /* ---------- entries of the selected month ---------- */
  const monthEntries = useMemo(
    () =>
      month === "all" ? entries : entries.filter((e) => monthOf(e) === month),
    [entries, month],
  );

  /* ---------- one row per employee ---------- */
  const rows = useMemo(() => {
    const byUser = new Map();

    monthEntries.forEach((e) => {
      const id = String(e.user);
      const g = byUser.get(id) || {
        name: e.employee,
        entries: 0,
        hours: 0,
        days: new Set(),
      };
      g.entries += 1;
      g.hours += Number(e.hours || 0);
      g.days.add(e.date);
      byUser.set(id, g);
    });

    const list = employees.map((u) => {
      const id = String(u._id);
      const g = byUser.get(id);
      return {
        id,
        name: u.name,
        entries: g ? g.entries : 0,
        hours: g ? g.hours : 0,
        days: g ? g.days.size : 0,
      };
    });

    // people who have entries but are not in the employee list any more
    // (admins are already removed from the entries above)
    const known = new Set(list.map((r) => r.id));
    byUser.forEach((g, id) => {
      if (!known.has(id)) {
        list.push({
          id,
          name: g.name,
          entries: g.entries,
          hours: g.hours,
          days: g.days.size,
        });
      }
    });

    const q = search.trim().toLowerCase();

    return list
      .filter((r) => !q || r.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, monthEntries, search]);

  const monthLabel =
    month === "all" ? "All Months" : MONTH_NAMES[Number(month) - 1];

  /* ---------- detail of one employee ---------- */
  const detailEntries = useMemo(
    () =>
      selected
        ? monthEntries.filter((e) => String(e.user) === selected.id)
        : [],
    [monthEntries, selected],
  );

  const detailHours = detailEntries.reduce(
    (s, e) => s + Number(e.hours || 0),
    0,
  );

  // hours by department > category
  const categoryChips = useMemo(() => {
    const map = new Map();

    detailEntries.forEach((e) => {
      const label = `${e.department || "Other"} - ${e.category || "No category"}`;
      map.set(label, (map.get(label) || 0) + Number(e.hours || 0));
    });

    return Array.from(map.entries())
      .map(([label, hours]) => ({ label, hours }))
      .sort((a, b) => b.hours - a.hours);
  }, [detailEntries]);

  /* ---------- banner numbers ---------- */
  const activeEmployees = rows.filter((r) => r.entries > 0).length;
  const totalEntries = rows.reduce((s, r) => s + r.entries, 0);
  const totalHours = rows.reduce((s, r) => s + r.hours, 0);

  const stats = selected
    ? [
        { value: detailEntries.length, label: "Entries" },
        {
          value: new Set(detailEntries.map((e) => e.date)).size,
          label: "Days",
        },
        { value: fmt(detailHours), label: "Total Hours" },
      ]
    : [
        { value: rows.length, label: "Employees" },
        { value: activeEmployees, label: "With Entries" },
        { value: totalEntries, label: "Entries" },
        { value: fmt(totalHours), label: "Total Hours" },
      ];

  /* ---------- no access: only a notice ---------- */
  if (canSeeAll === false) {
    return (
      <div className="els-page">
        <header className="els-banner">
          <div>
            <h1>Employee Log Summary</h1>
            <p>Timesheet of all employees.</p>
          </div>
        </header>

        <div className="els-message">
          Your role is "{myRole || "not set"}". It does not have "View All
          Employees" under Employee Log Detail in Role &amp; Permission. Ask the
          admin to tick it for this role, then refresh this page.
        </div>
      </div>
    );
  }

  return (
    <div className="els-page">
      {/* ----- banner ----- */}
      <header className="els-banner">
        <div>
          <h1>{selected ? selected.name : "Employee Log Summary"}</h1>
          <p>
            {selected
              ? `Timesheet entries for ${monthLabel} ${year}.`
              : "Timesheet of all employees. Pick a month and open the logs of an employee."}
          </p>
        </div>

        <div className="els-stats">
          {stats.map((s) => (
            <div className="els-stat" key={s.label}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </header>

      {message && <div className="els-message">{message}</div>}

      {/* ----- filters ----- */}
      <section className="els-card els-filters">
        <div className="els-selects">
          <label className="els-select">
            <span>Year</span>
            <select value={year} onChange={(e) => setYear(e.target.value)}>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>

          <label className="els-select">
            <span>Month</span>
            <select value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="all">All Months</option>
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={String(index + 1).padStart(2, "0")}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          <span className="els-chip">
            {monthLabel} {year}
          </span>
        </div>

        {selected ? (
          <button
            type="button"
            className="els-btn els-btn-light"
            onClick={() => setSelected(null)}
          >
            Back to all employees
          </button>
        ) : (
          <input
            className="els-search"
            type="search"
            placeholder="Search employee..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
      </section>

      {/* ================= ALL EMPLOYEES ================= */}
      {!selected && (
        <section className="els-card">
          <h3>Employees</h3>

          <div className="els-table-wrap">
            <table className="els-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Entries</th>
                  <th>Working Days</th>
                  <th>Total Hours</th>
                  <th className="els-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="els-empty">
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="els-empty">
                      No employees found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id}>
                      <td className="els-strong">{r.name}</td>
                      <td>
                        {r.entries || <span className="els-muted">0</span>}
                      </td>
                      <td>{r.days || <span className="els-muted">0</span>}</td>
                      <td>
                        {r.hours ? (
                          <span className="els-hours">{fmt(r.hours)}</span>
                        ) : (
                          <span className="els-muted">0</span>
                        )}
                      </td>
                      <td className="els-right">
                        <button
                          type="button"
                          className="els-btn els-btn-small"
                          onClick={() =>
                            setSelected({ id: r.id, name: r.name })
                          }
                        >
                          View Logs
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {!loading && rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td>{totalEntries}</td>
                    <td></td>
                    <td>
                      <span className="els-hours els-hours-total">
                        {fmt(totalHours)}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      )}

      {/* ================= ONE EMPLOYEE ================= */}
      {selected && (
        <>
          {categoryChips.length > 0 && (
            <section className="els-card">
              <h3>Category wise hours</h3>

              <div className="els-chips">
                {categoryChips.map((c) => (
                  <span className="els-cat" key={c.label}>
                    {c.label}
                    <b>{fmt(c.hours)} hrs</b>
                  </span>
                ))}
              </div>
            </section>
          )}

          <section className="els-card">
            <h3>
              Logs - {monthLabel} {year}
            </h3>

            <div className="els-table-wrap">
              <table className="els-table els-table-detail">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Department</th>
                    <th>Category</th>
                    <th>Project</th>
                    <th>Task</th>
                    <th>Sub-task</th>
                    <th>Hours</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {detailEntries.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="els-empty">
                        No entries in {monthLabel} {year}.
                      </td>
                    </tr>
                  ) : (
                    detailEntries.map((e) => (
                      <tr key={e._id}>
                        <td className="els-nowrap">{e.date}</td>
                        <td className="els-nowrap">
                          {e.startTime && e.endTime ? (
                            <span className="els-time">
                              {showTime(e.startTime)} - {showTime(e.endTime)}
                            </span>
                          ) : (
                            <span className="els-muted">Not saved</span>
                          )}
                        </td>
                        <td>
                          <span className="els-dept">{e.department}</span>
                        </td>
                        <td>{e.category || "-"}</td>
                        <td>{e.project || "-"}</td>
                        <td className="els-strong">{e.task}</td>
                        <td>{e.subTask || "-"}</td>
                        <td>
                          <span className="els-hours">{e.hours}</span>
                        </td>
                        <td className="els-notes-cell" title={e.notes || ""}>
                          {e.notes || "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {detailEntries.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={7}>Total hours</td>
                      <td>
                        <span className="els-hours els-hours-total">
                          {fmt(detailHours)}
                        </span>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
